import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form, ListGroup, Toast } from 'react-bootstrap';
import * as Sentry from '@sentry/node';
import axios from 'axios';
import camelcaseKeys from 'camelcase-keys';
import Link from 'next/link';
import LocalAPI from '../../SDKs/LocalAPI';
import { useFranchiseeContext } from '../../context/Franchisee';
import { SET_APPOINTMENT, useLeadContext } from '../../context/Lead';
import { usePaymentContext } from '../../context/Payment';
import { validateZipCode } from '../../utils/stringValidation';
import ToastWrapper from '../ToastWrapper';
import collectJsConfig from './collectJs.config';
import { useDispatch } from 'react-redux';
import { useSwipeable } from 'react-swipeable';
import Script from 'next/script';

interface BookingPaymentProps {
  tip?: any;
  checkboxValue: any;
  customerExists?: boolean;
  readyForPayment?: boolean;
}
const BookingPayment: React.FC<BookingPaymentProps> = ({
  tip,
  checkboxValue,
  readyForPayment,
}: BookingPaymentProps) => {
  const router = useRouter();
  const { franchisee } = useFranchiseeContext();
  const { lead, dispatchLead } = useLeadContext();
  const [disabled, setDisabled] = useState<boolean>(true);
  const [customerVaultId, setCustomerVaultId] = useState<string>('');
  const { paymentState, sendToPaymentState } = usePaymentContext();
  const [franchiseeSlug, setFranchiseeSlug] = useState<string>(
    franchisee.slug.toUpperCase().replaceAll('-', '_')
  );
  // const [appointmentState, setAppointment] = useState({});]

  const { CollectJS } = global;

  const [showSuccessToast, setShowSuccessToast] = useState<boolean>(false);

  const [showInfoToast, setShowInfoToast] = useState<boolean>(false);
  const [infoMessage, setInfoMessage] = useState<string>('');

  const [showErrorToast, setShowErrorToast] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const [cardValidated, setCardValidated] = useState<boolean>(false);
  const [cardExpValidated, setCardExpValidated] = useState<boolean>(false);
  const [cvvValidated, setCvvValidated] = useState<boolean>(false);

  const [showBookedToast, setShowBookedToast] = useState<boolean>(false);
  const [bookedToastMessage, setBookedToastMessage] = useState<string>('');
  const [counter, setCounter] = useState<number>(null);

  const [outOfAreaToast, setOutOfAreaToast] = useState<boolean>(false);
  const [outOfAreaToastMesssage, setOutOfAreaToastMessage] =
    useState<string>('');

  const dispatch = useDispatch();

  const cardFieldsAreValid = useCallback(
    () => cardValidated && cardExpValidated && cvvValidated,
    [cardValidated, cardExpValidated, cvvValidated]
  );

  // if the form reloads
  const resetCardValidation = () => {
    setCardValidated(false);
    setCardExpValidated(false);
    setCvvValidated(false);
  };

  // Check if the timeblock that was selected is still available
  useEffect(() => {
    try {
      axios
        .post('/api/get-free-time-blocks/', {
          leadId: lead.id,
          date: lead.dateTime,
        })
        .then((res) => {
          const blocks = res.data.data;
          // console.log(blocks);
          if (
            blocks &&
            Object.values(blocks).filter((item) => item === null).length === 3
          ) {
            // console.log(router);
            router.push({
              query: { fullyBooked: true },
            });
          }
        })
        .catch((err) => console.log(err));
    } catch (err) {
      console.log(err);
      Sentry.captureException(err);
    }
  }, [lead.zipCode]);

  useEffect(() => {
    if (router.query.fullyBooked) {
      setBookedToastMessage(
        'This time slot has just been booked. Please choose another time slot.'
      );

      setCounter(5);
      setShowBookedToast(true);
      setTimeout(() => {
        router.push('/schedule');
      }, 5000);

      setDisabled(true);
    }
  }, [router.query.fullyBooked]);

  useEffect(() => {
    if (counter > 0) {
      const timer = setInterval(() => setCounter(counter - 1), 1000);
      return () => clearInterval(timer);
    }
  }, [counter]);

  // Check if the selected franchise is in the list of locations associated with the zip code enterered
  useEffect(() => {
    try {
      if (lead.addressLineOne === '') {
        return;
      }
      // if (franchisee.id === 14 || lead.id === -1) {
      //   return;
      // }
      axios
        .post('/api/get-franchisee-zones/', {
          franchiseeId: franchisee.id.toString(),
        })
        .then((res) => {
          const franchiseeZones = res.data.data;
          if (franchiseeZones.find((zip) => zip === lead.zipCode)) {
            // console.log(`There's a match!`);
            setOutOfAreaToast(false);
          } else {
            axios
              .post('/api/get-locations/', {
                zipCode: lead.zipCode,
              })
              .then((res) => {
                setDisabled(true);
                setShowErrorToast(false);

                if (res.data.message === 'Found zone exclusive stores') {
                  if (!res.data.data.length) {
                    setOutOfAreaToast(true);
                    setDisabled(true);
                    setOutOfAreaToastMessage(
                      "We cannot book online at this time. We didn't find any Two Maids stores in your area. "
                    );
                    return;
                  }
                  setOutOfAreaToast(true);
                  setDisabled(true);
                  setOutOfAreaToastMessage(
                    `We cannot book online at this time. The zip code you have entered is zoned for ${res.data.data[0].name}, ${res.data.data[0].state}, but you have selected ${franchisee.name}, ${franchisee.state}. `
                  );
                } else if (
                  res.data.message ===
                    'No stores were found within a distance radius' ||
                  res.data.data.length === 0
                ) {
                  setOutOfAreaToast(true);
                  setDisabled(true);
                  setOutOfAreaToastMessage(
                    "We cannot book online at this time. We didn't find any Two Maids stores in your area. "
                  );
                } else if (
                  res.data.message === 'Found Locations in a distance radius'
                ) {
                  setOutOfAreaToast(true);
                  setDisabled(true);
                  setOutOfAreaToastMessage(
                    `We cannot book online at this time. The zip code you have entered is not enabled for online booking. `
                  );
                }
              });
          }
        })
        .catch((err) => {
          console.log(err);
          Sentry.captureException(err);
        });
    } catch (err) {
      Sentry.captureException(err);
    }
  }, [lead.zipCode, lead.addressLineOne]);

  useEffect(() => {
    setFranchiseeSlug(franchisee.slug.toUpperCase().replaceAll('-', '_'));
  }, [franchisee]);

  // update CollectJS callback
  useEffect(() => {
    if (
      CollectJS !== undefined &&
      lead.addressLineOne &&
      validateZipCode(lead.zipCode)
    ) {
      resetCardValidation();
      setDisabled(false);
      CollectJS.configure({
        ...collectJsConfig,
        validationCallback: (paylineField, validated, message) => {
          switch (paylineField) {
            case 'ccnumber':
              setCardValidated(validated);
              break;
            case 'ccexp':
              setCardExpValidated(validated);
              break;
            case 'cvv':
              setCvvValidated(validated);
              break;
            default:
              break;
          }
          if (!validated) {
            if (message.includes('Field is empty')) {
              setErrorMessage(
                `${lead.firstName}, it looks like you have left a required field blank.\nPlease make sure that you have entered all of the required credit card information, including the credit card number, expiration date, and security code. If you continue to experience this issue, please contact our local office @ ${franchisee.phonePrimary} for further assistance.`
              );
            } else {
              setErrorMessage(message);
            }
            setShowErrorToast(true);
            Sentry.captureException(`${lead ?? ' '} ${message}}`);
          } else {
            setShowErrorToast(false);
          }
        },
        callback: async (response) => {
          const paylineToken: string = response.token;
          let setPaymentMethodData;

          let billingInfo = {
            sameAddress: true,
            billingAddressLineOne: '',
            billingAddressLineTwo: '',
            billingCity: '',
            billingState: '',
            billingZipCode: '',
            ipAddress: '',
          };
          try {
            const billingAddressData = await axios.get(
              '/api/billing-address-session/'
            );
            if (!billingAddressData?.data.message.includes('Error')) {
              billingInfo = billingAddressData.data.data.billingAddress;
            }
          } catch (e) {
            console.log(e);
            Sentry.captureException(e);
          }

          const {
            sameAddress,
            billingAddressLineOne,
            billingAddressLineTwo,
            billingCity,
            billingState,
            billingZipCode,
            ipAddress,
          } = billingInfo;

          const hasAllRequiredAddressComponents = [
            billingAddressLineOne,
            billingCity,
            billingState,
            billingZipCode,
          ].every(Boolean);

          if (sameAddress || !hasAllRequiredAddressComponents) {
            setPaymentMethodData = {
              paylineToken,
              firstName: lead.firstName,
              lastName: lead.lastName,
              price: parseFloat(lead.totalPrice.toString()),
              addressLineOne: lead.addressLineOne,
              addressLineTwo: lead.addressLineTwo,
              city: lead.city,
              state: lead.state,
              zipCode: lead.zipCode,
              phonePrimary: lead.phonePrimary,
              email: lead.email,
              franchiseeSlug: franchisee.slug,
              vaultId: customerVaultId || null,
              ipAddress,
              shippingAddressOne: lead?.addressLineOne ?? '',
              shippingAddressTwo: lead?.addressLineTwo ?? '',
              shippingCity: lead?.city ?? '',
              shippingZipCode: lead?.zipCode,
              shippingState: lead.state,
            };
          } else {
            setPaymentMethodData = {
              paylineToken,
              firstName: lead.firstName,
              lastName: lead.lastName,
              price: parseFloat(lead.totalPrice.toString()),
              addressLineOne: billingAddressLineOne ?? '',
              addressLineTwo: billingAddressLineTwo ?? '',
              city: billingCity ?? '',
              state: billingState ?? '',
              zipCode: billingZipCode ?? '',
              phonePrimary: lead.phonePrimary,
              email: lead.email,
              franchiseeSlug: franchisee.slug,
              vaultId: customerVaultId || null,
              ipAddress,
              shippingAddressOne: lead.addressLineOne,
              shippingAddressTwo: lead.addressLineTwo,
              shippingCity: lead.city,
              shippingZipCode: lead.zipCode,
              shippingState: lead.state,
            };
          }
          await LocalAPI.setPaymentMethod(setPaymentMethodData)
            .then((result) => {
              const errorMessage = {
                result,
                lead,
                franchisee,
                billingInfo,
                setPaymentMethodData,
              };
              if (result.status === 201) {
                const vaultId: string = result?.data.data.customer_vault_id;
                setCustomerVaultId(vaultId);
                sendToPaymentState('RESOLVE');
                setInfoMessage('Card Info Set.  Scheduling Appointment');
                setShowInfoToast(true);
              } else if (customerVaultId !== '') {
                Sentry.captureException(result, {
                  extra: errorMessage,
                  tags: {
                    errorType: 'Card REJECT/DUPLICATE/NO VAULT ID',
                  },
                });
                sendToPaymentState('REJECT');
                sendToPaymentState('DUPLICATE');
              } else {
                setErrorMessage('Your Card was Declined');

                axios
                  .post(
                    'https://gataware.com/rest-api/error-email/',
                    errorMessage
                  )
                  .then((res) => console.log(res))
                  .catch((err) => console.log(err));

                Sentry.captureException(result, {
                  extra: errorMessage,
                  tags: {
                    errorType: 'Card Declined',
                  },
                });
                setShowErrorToast(true);
                sendToPaymentState('REJECT');
                sendToPaymentState('DECLINED');
              }

              return result;
            })
            .catch((error) => {
              Sentry.captureException('REJECT/ERROR', error);
              sendToPaymentState('REJECT');
              sendToPaymentState('ERROR');

              return error;
            });
        },
      });

      setTimeout(() => {
        const ccInputFrame: HTMLElement = document.querySelector(
          '#CollectJSInlineccnumber'
        );
        // if (ccInputFrame === null) location.reload();
        ccInputFrame?.setAttribute('frameBorder', '');

        const expInputFrame: HTMLElement = document.querySelector(
          '#CollectJSInlineccexp'
        );
        expInputFrame?.setAttribute('frameBorder', '');

        const cvvInputFrame: HTMLElement = document.querySelector(
          '#CollectJSInlinecvv'
        );
        cvvInputFrame?.setAttribute('frameBorder', '');
      }, 300);
    }
  }, [
    lead?.addressLineOne,
    lead?.addressLineTwo,
    lead?.city,
    lead?.state,
    lead?.zipCode,
    readyForPayment,
  ]);

  useEffect(() => {
    if (cardFieldsAreValid()) {
      sendToPaymentState('CARD_VALID');
      sendToPaymentState('READY');
    } else {
      sendToPaymentState('CARD_INVALID');
    }
  }, [cardValidated, cardExpValidated, cvvValidated]);

  useEffect(() => {
    const fetchData = async () => {
      switch (true) {
        case paymentState.matches('transactionPending'):
          CollectJS.startPaymentRequest();
          break;
        case paymentState.matches('schedulePending'):
          if (customerVaultId !== '') {
            setInfoMessage('Scheduling Appointment');
            setShowInfoToast(true);
            // Swapped the api out.
            try {
              const { data, status } = await axios.post('/api/schedule-now/', {
                vaultId: parseInt(customerVaultId, 10),
                leadId: lead.id,
                checkboxChecked: checkboxValue,
              });
              const responseData = camelcaseKeys(data.data);

              switch (status) {
                case 200:
                  sendToPaymentState('SUCCESS');
                  dispatchLead({
                    type: SET_APPOINTMENT,
                    payload: {
                      id: lead.id,
                      franchisee: franchisee.id,
                      dateTime: lead.dateTime,
                      appointment: responseData,
                    },
                  });
                  setInfoMessage("Success! We've booked your appointment");
                  setShowInfoToast(true);
                  try {
                    const response = await axios.post('/api/tips-session/', {
                      tipsData: {
                        tips: tip,
                        id: responseData.id,
                        customer: responseData.customer,
                      },
                    });
                  } catch (error) {
                    console.log(error);
                  }

                  router.push('/thank-you/');

                  break;
                default:
                  sendToPaymentState('REJECT');
                  sendToPaymentState('ERROR');
                  break;
              }
            } catch (error) {
              Sentry.captureException(error);
              sendToPaymentState('REJECT');
              sendToPaymentState('ERROR');
              setErrorMessage(
                'There was an issue scheduling your appointment.'
              );
              setShowErrorToast(true);
            }
          }
          break;
        case paymentState.matches('scheduleSuccess'):
          break;
        case paymentState.matches('scheduleFail'):
        default:
          break;
      }
    };
    fetchData();
  }, [paymentState]);

  const handlers = useSwipeable({
    onSwiped: () => setShowErrorToast(false),
  });

  function formatPhoneNumber(phoneNumber) {
    const phoneNumberRegex = /@?\s*([\d]{10})\s*/g;
    const matches = phoneNumberRegex.exec(phoneNumber);

    if (matches !== null) {
      const rawPhoneNumber = matches[1];
      return `(${rawPhoneNumber.substring(0, 3)}) ${rawPhoneNumber.substring(
        3,
        6
      )}-${rawPhoneNumber.substring(6, 10)}`;
    } else {
      return phoneNumber;
    }
  }

  return (
    <>
      {franchisee?.franchiseePreferences?.collectJsToken &&
        (process.env.TWOMAIDS_ENV === 'preview' ||
          process.env.TWOMAIDS_ENV === 'production') && (
          <Script
            src='https://secure.paylinedatagateway.com/token/Collect.js'
            data-tokenization-key={
              franchisee.franchiseePreferences.collectJsToken
            }
            data-variant='inline'
            data-field-ccnumber-placeholder='0000 0000 0000 0000'
            data-field-ccexp-placeholder='MM/YY'
            data-field-cvv-placeholder='123'
            data-custom-css='
              {"border-color":"rgb(206, 212, 218)",
              "padding":"0.375rem 0.75rem",
              "border": "2px solid rgb(206, 212, 218)",
              "border-style":"solid",
              "border-radius": "0.25rem",
              "font-size": "1rem",
              "height":"40px"}'
          />
        )}
      {/*
            DEBUG-TMF.
            Data Tokenization Key needs to prefereably come from DB or Env Variables.
          */}
      {process.env.NODE_ENV === 'development' && (
        <Script
          src='https://secure.paylinedatagateway.com/token/Collect.js'
          data-tokenization-key='NC6KwR-24CtwS-mcqyhr-E7AzjR'
          data-variant='inline'
          data-field-ccnumber-placeholder='0000 0000 0000 0000'
          data-field-ccexp-placeholder='MM/YY'
          data-field-cvv-placeholder='123'
          data-custom-css='
            {"border-color":"rgb(206, 212, 218)",
            "padding":"0.375rem 0.75rem",
            "border": "2px solid rgb(206, 212, 218)",
            "border-style":"solid",
            "border-radius": "0.25rem",
            "font-size": "1rem",
            "height":"40px"}'
        />
      )}
      {lead?.step >= 5 ? (
        <>
          <Form
            id='booking-form-payment'
            className={`needs-validation book__gated ${disabled ? '' : 'active'}`}
            noValidate
          >
            <div className='font-weight-bold mt-3 mb-2 text-left form-section-header'>
              Payment Information
            </div>
            <p className='booking-info-text text-left'>
              Your card will not be charged until the date of your appointment.
            </p>
            <Form.Row className='align-items-start'>
              <Form.Group className='col-12 col-md-6'>
                <div
                  id='ccnumber'
                  style={{ height: 'calc(1.5em + .75rem + 4px)' }}
                />
                <Form.Control.Feedback type='invalid'>
                  Please enter a valid credit card number.
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group className='col-6 col-md-3'>
                <div
                  id='ccexp'
                  style={{ height: 'calc(1.5em + .75rem + 4px)' }}
                />
                <Form.Control.Feedback type='invalid'>
                  Please enter an expiration date.
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group className='col-6 col-md-3'>
                <div id='cvv' style={{ height: 'calc(1.5em + .75rem + 4px)' }} />
                <Form.Control.Feedback type='invalid'>
                  Please enter a CVC.
                </Form.Control.Feedback>
              </Form.Group>
            </Form.Row>
          </Form>
          <div className='cc-provider-badge-container'>
            <img src="https://res.cloudinary.com/twomaidsengi/image/upload/v1742589672/Visa_dqtlkb.webp" alt="Visa" />
            <img src="https://res.cloudinary.com/twomaidsengi/image/upload/v1742589672/mastercard_u4wd9k.webp" alt="Mastercard" />
            <img src="https://res.cloudinary.com/twomaidsengi/image/upload/v1742589672/Discover_x78dvz.webp" alt="Discover" />
            <img src="https://res.cloudinary.com/twomaidsengi/image/upload/v1742589672/AmericanExpress_a8rqed.webp" alt="American Express" />
          </div>
        </>
      ) : null}
      <ToastWrapper className='confirm-toast-wrapper'>
        <Toast
          show={showSuccessToast}
          onClose={() => setShowSuccessToast(false)}
          autohide
          delay={8000}
        >
          <Toast.Body>
            <ListGroup>
              <ListGroup.Item variant='success'>
                Payment has been processed successfully
              </ListGroup.Item>
            </ListGroup>
          </Toast.Body>
        </Toast>
        <Toast
          show={showInfoToast}
          onClose={() => setShowInfoToast(false)}
          autohide
          delay={8000}
        >
          <Toast.Body>
            <ListGroup>
              <ListGroup.Item variant='success'>{infoMessage}</ListGroup.Item>
            </ListGroup>
          </Toast.Body>
        </Toast>

        <Toast
          show={showErrorToast}
          onClose={() => setShowErrorToast(false)}
          // autohide
          delay={8000}
          {...handlers}
          style={{ backgroundColor: '#cf2680' }}
        >
          <Toast.Body>
            <Button
              type='button'
              style={{ color: 'white' }}
              onClick={() => setShowErrorToast(false)}
            >
              X
            </Button>

            <ListGroup>
              <ListGroup.Item className='toast-error'>
                {errorMessage.length > 2 ? (
                  <>
                    {errorMessage.includes(' field blank') ? (
                      <div style={{ textAlign: 'left' }}>
                        <p>{errorMessage.split('.')[0]}.</p>
                        <p>{errorMessage.split('.')[1]}.</p>
                        <p>
                          {errorMessage.split('.')[2].split('@')[0]}{' '}
                          <span>
                            {' '}
                            <a
                              style={{
                                color: '#fff',
                                textDecoration: 'underline',
                                fontWeight: 'bold',
                              }}
                              href={`tel:${franchisee.phonePrimary}`}
                            >
                              {' '}
                              {formatPhoneNumber(franchisee.phonePrimary)}{' '}
                            </a>
                            for further assistance.
                          </span>
                        </p>
                      </div>
                    ) : (
                      errorMessage
                    )}
                  </>
                ) : (
                  'Payment declined. Error!'
                )}
              </ListGroup.Item>
            </ListGroup>
          </Toast.Body>
        </Toast>

        <Toast
          show={showBookedToast}
          onClose={() => setShowBookedToast(false)}
          autohide
          delay={5000}
        >
          {showBookedToast && (
            <Toast.Body
              className='toast-error'
              style={{ background: 'var(--dark-pink)' }}
            >
              {bookedToastMessage}
              <span className='d-block'>Redirecting in: {counter}</span>
            </Toast.Body>
          )}
        </Toast>

        <Toast
          show={outOfAreaToast}
          onClose={() => setOutOfAreaToast(false)}
          autohide={false}
        >
          {outOfAreaToast && (
            <Toast.Body
              className='toast-error'
              style={{
                background: 'var(--dark-pink)',
                marginLeft: '1rem',
                maxWidth: '500px',
              }}
            >
              <span>{outOfAreaToastMesssage}</span>
              <br />
              <br />
              <span>
                To reach your nearest Two Maids location visit our <a href='/locations' style={{color: '#fff', textDecoration: 'underline'}}>directory</a>.
              </span>
            </Toast.Body>
          )}
        </Toast>
      </ToastWrapper>
    </>
  );
};

export default BookingPayment;
